import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import BookingForm from "../components/BookingForm";
import { TOURS, IMAGES } from "../data/content";
import { useT, useLang } from "../i18n/LanguageContext";
import { localizeTours } from "../i18n/content_it";

export default function CruiseTours() {
  const [selected, setSelected] = useState(null);
  const tr = useT();
  const { lang } = useLang();
  const tours = localizeTours(TOURS, lang);

  return (
    <div className="pt-28">
      <Seo
        title={tr("seo.journeys.title")}
        description={tr("seo.journeys.desc")}
        locale={lang === "it" ? "it_IT" : "en_US"}
        path="/cruise-tours"
      />

      <section className="px-6 md:px-12 max-w-[1600px] mx-auto">
        <Reveal>
          <p className="overline mb-4">{tr("journeys.overline")}</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light leading-none max-w-4xl">
            {tr("journeys.title")}
          </h1>
          <p className="text-white/60 mt-8 max-w-2xl leading-relaxed">
            {tr("journeys.intro")}
          </p>
        </Reveal>
      </section>

      <section className="px-6 md:px-12 max-w-[1600px] mx-auto mt-20 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
        {tours.map((t, i) => (
          <Reveal key={t.id} delay={(i % 3) * 0.08}>
            <article data-testid={`tour-card-${t.id}`} className="group flex flex-col h-full bg-[#121212] border border-white/8 hover:border-[#C5A059]/40 transition-colors duration-500">
              <div className="relative h-60 overflow-hidden">
                <img src={t.image} alt={t.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                <span className="absolute top-4 left-4 overline bg-black/50 backdrop-blur-sm px-3 py-1">{t.duration}</span>
              </div>
              <div className="p-7 flex flex-col flex-1">
                <h2 className="font-serif text-2xl md:text-3xl mb-3">{t.title}</h2>
                <p className="text-white/60 text-sm leading-relaxed">{t.description}</p>
                {t.highlights && (
                  <ul data-testid={`tour-highlights-${t.id}`} className="mt-5 space-y-2 flex-1">
                    {t.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2.5 text-sm text-white/75">
                        <Check size={15} className="text-[#C5A059] mt-0.5 shrink-0" strokeWidth={2.2} />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  onClick={() => setSelected(t)}
                  data-testid={`tour-book-${t.id}`}
                  className="group/btn mt-7 inline-flex items-center gap-2 self-start border border-[#C5A059] text-[#C5A059] px-6 py-3 uppercase tracking-[0.2em] text-xs hover:bg-[#C5A059] hover:text-[#0A0A0A] transition-colors"
                >
                  {tr("journeys.book")} <ArrowUpRight size={15} className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                </button>
              </div>
            </article>
          </Reveal>
        ))}
      </section>

      <section className="relative h-[50vh] mt-32 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-fixed-hero" style={{ backgroundImage: `url(${IMAGES.tuscanyHills})` }} />
        <div className="absolute inset-0 bg-black/65" />
        <Reveal className="relative z-10 text-center px-6">
          <p className="font-serif text-3xl sm:text-5xl font-light max-w-3xl mx-auto">
            {tr("journeys.ctaHeadline")}
          </p>
        </Reveal>
      </section>

      <AnimatePresence>
        {selected && <BookingForm tour={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </div>
  );
}
